import { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { useElementDecorator } from '../FormRenderCtx';
import FieldTitle from '../FieldTitle';
import FormHelperText from '../FormHelperText';
import { useClassName } from '../hooks';
import { withFieldWrapper } from '../fieldWrapper';
import { ConditionalPropertyPropType } from '../propTypes';

const TimeRangePickerElement = forwardRef(function TimeRangePickerElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    title,
    value,
    disabled,
    style,
    componentProps = {},
    fieldTitleProps,
    validation,
    onChange,
    onTouchChanged,
    status,
    helpers,
  } = props;

  const ElementDecorator = useElementDecorator();

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);

  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const innerValue = (() => {
    if (!Array.isArray(value)) {
      return [];
    }

    if (
      !(typeof value[0] === 'string' && value[0].length) ||
      !(typeof value[1] === 'string' && value[1].length)
    ) {
      return ['', ''];
    }

    return [dayjs(value[0], 'HH:mm:ss'), dayjs(value[1], 'HH:mm:ss')];
  })();

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'time-range-picker-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <div>
          <TimePicker.RangePicker
            {...componentProps}
            className={classNameComp}
            name={id}
            value={innerValue}
            disabled={disabled}
            onChange={(time, timeStr) => onChange(timeStr)}
            onBlur={onTouchChanged}
            status={status}
          />
          <FormHelperText helpers={helpers} />
        </div>
      </div>
    </ElementDecorator>
  );
});

TimeRangePickerElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
TimeRangePickerElement.displayName = 'TimeRangePicker';

export default withFieldWrapper(TimeRangePickerElement);
